/**
 * Hook for the manual creator payout approval queue.
 *
 * Mirrors useApprovals (submission payouts). Operations:
 *
 *   1. fetchPendingManualPayoutApprovals — GET /payout/approvals/pending
 *   2. stampManualPayoutApproval(approvalId) — POST /payout/:id/approval/stamp
 *      Server derives role from user flags. Second stamp auto-fires Stripe.
 *      onSuccess re-fetches so paid records disappear from the table.
 *   3. rejectManualPayoutApproval(approvalId) — POST /payout/:id/approval/reject
 *      Manual payouts do NOT auto-clear on resubmit — a new manual payout is
 *      always a brand new record, preserving rejected ones as audit trail.
 *   4. bulkStampManualPayoutApprovals / bulkRejectManualPayoutApprovals
 *      Same shape as the submission hook's bulk methods: fire N silent requests,
 *      then re-fetch once and pop a single summary toast.
 */
import { useDispatch, useSelector } from 'react-redux';

import {
  manualPayoutApprovalsLoaded,
  selectManualPayoutApprovals,
} from 'features/approvals/manualPayoutApprovals.slice';
import { apiAction } from 'shared/actions/api';
import { successToastActivated } from 'shared/actions/toasts';
import { RESOURCES } from 'shared/config/resourceNames';
import { API_APPROVAL_ROUTES } from 'shared/config/routes/api/apiApprovalRoutes';

interface StampOptions {
  silent?: boolean;
  skipRefetch?: boolean;
}

export const useManualPayoutApprovals = () => {
  const dispatch = useDispatch();
  const manualPayoutApprovals = useSelector(selectManualPayoutApprovals);

  const fetchPendingManualPayoutApprovals = () => {
    dispatch(
      apiAction({
        path: { route: API_APPROVAL_ROUTES.PENDING_MANUAL_PAYOUT_APPROVALS },
        method: 'GET',
        entity: RESOURCES.PAYOUT,
        successAction: manualPayoutApprovalsLoaded,
      }),
    );
  };

  const stampManualPayoutApproval = (approvalId: number, options: StampOptions = {}) => {
    dispatch(
      apiAction({
        path: {
          route: API_APPROVAL_ROUTES.STAMP_MANUAL_PAYOUT_APPROVAL,
          variables: { approvalId },
        },
        method: 'POST',
        data: {},
        entity: RESOURCES.PAYOUT,
        ...(options.silent ? {} : { successToast: { message: 'Approval stamped successfully' } }),
        ...(options.skipRefetch ? {} : { onSuccess: fetchPendingManualPayoutApprovals }),
      }),
    );
  };

  const rejectManualPayoutApproval = (approvalId: number, options: StampOptions = {}) => {
    dispatch(
      apiAction({
        path: {
          route: API_APPROVAL_ROUTES.REJECT_MANUAL_PAYOUT_APPROVAL,
          variables: { approvalId },
        },
        method: 'POST',
        data: {},
        entity: RESOURCES.PAYOUT,
        ...(options.silent ? {} : { successToast: { message: 'Approval rejected' } }),
        ...(options.skipRefetch ? {} : { onSuccess: fetchPendingManualPayoutApprovals }),
      }),
    );
  };

  /**
   * Superadmin manual retry — POST /payout/:id/approval/execute. Same recovery path as the
   * submission queue's retryApproval, for a manual payout that failed at Stripe after both stamps.
   */
  const retryManualPayoutApproval = (approvalId: number) => {
    dispatch(
      apiAction({
        path: {
          route: API_APPROVAL_ROUTES.EXECUTE_MANUAL_PAYOUT_APPROVAL,
          variables: { approvalId },
        },
        method: 'POST',
        data: {},
        entity: RESOURCES.PAYOUT,
        successToast: { message: 'Payment retried' },
        onSuccess: fetchPendingManualPayoutApprovals,
      }),
    );
  };

  const bulkStampManualPayoutApprovals = (approvalIds: number[], skippedCount = 0) => {
    if (!approvalIds.length && skippedCount === 0) return;
    approvalIds.forEach((id) =>
      stampManualPayoutApproval(id, { silent: true, skipRefetch: true }),
    );
    fetchPendingManualPayoutApprovals();
    const summary =
      skippedCount > 0
        ? `Stamped ${approvalIds.length}, skipped ${skippedCount} already-stamped.`
        : `Stamped ${approvalIds.length} manual payouts.`;
    dispatch(successToastActivated({ message: summary }));
  };

  const bulkRejectManualPayoutApprovals = (approvalIds: number[]) => {
    if (!approvalIds.length) return;
    approvalIds.forEach((id) =>
      rejectManualPayoutApproval(id, { silent: true, skipRefetch: true }),
    );
    fetchPendingManualPayoutApprovals();
    dispatch(successToastActivated({ message: `Rejected ${approvalIds.length} manual payouts.` }));
  };

  return {
    manualPayoutApprovals,
    fetchPendingManualPayoutApprovals,
    stampManualPayoutApproval,
    rejectManualPayoutApproval,
    retryManualPayoutApproval,
    bulkStampManualPayoutApprovals,
    bulkRejectManualPayoutApprovals,
  };
};
